import { WritableAtom } from 'jotai';
import { merge } from 'lodash-es';
import { SetStateAction, useMemo } from 'react';
import { KpiSection } from '../../../../../../data-models/company-financials.data-model';
import { ICompanyDataModel } from '../../../../../../data-models/company.data-model';
import {
  IKPIDataValue,
  IKPIRequestDataModel,
  IKPIRequestResponse,
  IKPIResponseFormSectionData,
  KPIRequestResponse,
  KPIRequestStatus,
} from '../../../../../../data-models/kpi-requests.data-model';
import {
  acceptKPIRequestResponse,
  fetchKPIRequestById,
  fetchKPIRequestResponseById,
  moveRequestBackToReview,
  rejectKPIRequestResponse,
  updateKPIRequestResponse,
} from '../../../../../../services/queries/KPIRequestsQueries';
import { IKPITemplate } from '../../../../../../data-models/kpi-template.data-model';
import { fetchKPITemplateById } from '../../../../../../services/queries/KPITemplatesQueries';
import { fetchCompanyRaw } from '../../../../../../services/queries/MaggieCompanyQueries';
import { getForesightStore } from '../../../../../../util/jotai-store';
import { getTemplateTextualKpisMap, mergeUpdatedTextualKpiData } from '../../../KpiTemplateTextualFieldUtils';
import { useShouldSaveMsEnteredFinancials } from '../managed-services-utils';
import { useSaveMsEnteredFinancials } from '../msFinancialsGridActions';
import { useSelectDeleteRequest } from '../../components/DeleteRequest';
import { companyStateJ } from '../../../../../../services/state/CompanyStateJ';
import { getErrorMessage } from '../../../../../../services/queryHelpers';
import { useToastMessageState } from '../../../../../../components/ToastMessage/ToastMessageProvider';
import { removeEmptyValuesFromKpiData } from '../../../../utils';

export type KPIReviewState = ReviewerViewData & {
  isModified: boolean;
  isSubmitting: boolean;
  submissionError: string | null;
};

export type KPIReviewStateAtom = WritableAtom<KPIReviewState, [SetStateAction<KPIReviewState>], unknown>;

export function useKPIReviewerActions(stateAtom: KPIReviewStateAtom, store = getForesightStore()) {
  const { get, set } = store;
  const shouldSaveMsEnteredFinancials = useShouldSaveMsEnteredFinancials();
  const saveMsEnteredFinancials = useSaveMsEnteredFinancials();
  const onSelectDeleteRequest = useSelectDeleteRequest();
  const { pushErrorToast } = useToastMessageState();

  return useMemo(() => {
    const onDeleteRequest = () => {
      const { currentRequest } = get(stateAtom);
      onSelectDeleteRequest(currentRequest);
    };

    const onKPIGridDataUpdated = async (section: KpiSection, updatedData: IKPIDataValue[]) => {
      const { currentTemplate, currentResponse } = get(stateAtom);
      const templateTextualKpis = getTemplateTextualKpisMap(currentTemplate);
      const newKPIData = currentResponse.kpiData.values
        .filter((data) => data.section !== section || templateTextualKpis.has(data.kpiId))
        .concat(updatedData);

      set(stateAtom, (prev) => {
        return {
          ...prev,
          isModified: true,
          currentResponse: merge({}, currentResponse, { kpiData: { values: newKPIData } }),
        };
      });
    };

    const onTextualKpiUpdated = async (updatedData: IKPIDataValue) => {
      const { currentResponse } = get(stateAtom);
      const newKpiData = mergeUpdatedTextualKpiData(updatedData, currentResponse.kpiData.values);

      set(stateAtom, (prev) => {
        return {
          ...prev,
          isModified: true,
          currentResponse: merge({}, currentResponse, { kpiData: { values: newKpiData } }),
        };
      });
    };

    const onSectionDataUpdated = async (updatedData: IKPIResponseFormSectionData) => {
      const { currentResponse } = get(stateAtom);
      const newSectionData = currentResponse.sectionData.map((section) => {
        if (section.sectionId === updatedData.sectionId) {
          return updatedData;
        }
        return section;
      });

      set(stateAtom, (prev) => {
        return {
          ...prev,
          isModified: true,
          currentResponse: { ...currentResponse, sectionData: newSectionData },
        };
      });
    };

    const acceptResponse = async (comment?: string) => {
      const { currentRequest, currentResponse, isModified } = get(stateAtom);
      set(stateAtom, (prev) => ({ ...prev, isSubmitting: true }));

      let updatedRequest: IKPIRequestDataModel;
      try {
        if (isModified) {
          await updateKPIRequestResponse(
            currentRequest.id.toString(),
            removeEmptyValuesFromKpiData(currentResponse)
          );
        }

        updatedRequest = await acceptKPIRequestResponse(currentRequest.id.toString(), comment);
        if (shouldSaveMsEnteredFinancials(currentRequest.status)) {
          await saveMsEnteredFinancials(currentRequest);
        }
        set(stateAtom, (prev) => {
          return {
            ...prev,
            isSubmitting: false,
            currentRequest: { ...updatedRequest },
          };
        });
      } catch (err) {
        const errorMessage = getErrorMessage(err, 'Failed to accept response');
        pushErrorToast({ message: errorMessage });
        set(stateAtom, (prev) => ({
          ...prev,
          isSubmitting: false,
          submissionError: errorMessage,
        }));
      }
    };

    const rejectResponse = async (comment?: string) => {
      const { currentRequest } = get(stateAtom);
      set(stateAtom, (prev) => ({ ...prev, isSubmitting: true }));

      let updatedRequest: IKPIRequestDataModel;
      try {
        updatedRequest = await rejectKPIRequestResponse(currentRequest.id.toString(), comment);
        set(stateAtom, (prev) => {
          return {
            ...prev,
            isSubmitting: false,
            currentRequest: { ...updatedRequest },
          };
        });
      } catch (err) {
        const errorMessage = getErrorMessage(err, 'Failed to reject response');
        pushErrorToast({ message: errorMessage });
        set(stateAtom, (prev) => ({
          ...prev,
          isSubmitting: false,
          submissionError: errorMessage,
        }));
      }
    };

    const moveBackToReview = async () => {
      const { currentRequest } = get(stateAtom);
      set(stateAtom, (prev) => ({ ...prev, isSubmitting: true }));

      let updatedRequest: IKPIRequestDataModel;
      try {
        updatedRequest = await moveRequestBackToReview(currentRequest.id);
        set(stateAtom, (prev) => ({
          ...prev,
          isSubmitting: false,
          currentRequest: { ...updatedRequest },
        }));
      } catch (err) {
        const errorMessage = getErrorMessage(err, 'Failed to move back to review');
        pushErrorToast({ message: errorMessage });
        set(stateAtom, (prev) => ({
          ...prev,
          submissionError: errorMessage,
          isSubmitting: false,
        }));
      }
    };

    return {
      acceptResponse,
      moveBackToReview,
      onDeleteRequest,
      onKPIGridDataUpdated,
      onSectionDataUpdated,
      onTextualKpiUpdated,
      rejectResponse,
    };
  }, [
    get,
    stateAtom,
    onSelectDeleteRequest,
    set,
    shouldSaveMsEnteredFinancials,
    saveMsEnteredFinancials,
    pushErrorToast,
  ]);
}
export type KPIReviewerActions = ReturnType<typeof useKPIReviewerActions>;

type ReviewerViewBaseData = {
  company: ICompanyDataModel;
  currentRequest: NonNullable<KPIRequestResponse>;
};

export type ArchivedReviewerViewData = ReviewerViewBaseData & { archived: true };

export type ReviewerViewData = ReviewerViewBaseData & {
  archived: false;
  currentResponse: IKPIRequestResponse;
  currentTemplate: IKPITemplate;
};

export type ReviewerViewInitialData = ArchivedReviewerViewData | ReviewerViewData;

export async function loadReviewerViewInitialData(requestId: number): Promise<ReviewerViewInitialData> {
  const currentRequest = (await fetchKPIRequestById(requestId))!;
  const company = await fetchCompanyRaw(currentRequest.companyId);
  getForesightStore().set(companyStateJ(currentRequest.companyId), company);

  // Responses of archived ("deleted") requests stay hidden in the UI — don't fetch them.
  if (currentRequest.status === KPIRequestStatus.Archived) {
    return { archived: true, company, currentRequest };
  }

  const currentResponse = await fetchKPIRequestResponseById(currentRequest.id);
  const currentTemplate = (await fetchKPITemplateById(currentRequest.templateId)) as IKPITemplate;

  return {
    archived: false,
    company,
    currentRequest,
    currentResponse,
    currentTemplate,
  };
}
